/**
 * The camera path for face analysis: aim, a short countdown, a brief window, a suggestion.
 *
 * Everything happens on this device. Frames are drawn to a working canvas, reduced to a face outline,
 * measured, classified and dropped. What leaves is a shape, two scores, a handful of reason codes and
 * three version strings — no image, no landmark, no outline point, no ratio, and nothing per-frame.
 *
 * The window is short because a silhouette is stable where a limbus is not: PD needed five seconds of
 * agreeing frames, a face shape needs enough clean frames to agree on one outline. Two seconds is the
 * starting point 4C.2 suggested and is not settled — and it is not the dial to turn when a capture
 * fails. A capture that cannot pass the gates is telling the truth about the geometry.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaceShape,
  GateFailure,
  GATE_CONFIG_VERSION,
  CLASSIFIER_VERSION,
  GEOMETRY_VERSION,
  type FaceAnalysis,
} from '@optiflow/face-vision';
import { analyseDetection } from './analyseDetection';
import { CameraError, FRONT_CAMERA, openCamera, stopCamera } from '@/modules/capture/vision/camera';
import { loadFaceLandmarker, toFaces, type Landmarker } from '@/modules/capture/vision/mediapipe';

/** How long frames are collected once the countdown ends. A starting point, not a settled value. */
export const FACE_WINDOW_MS = 2_000;
const COUNTDOWN_MS = 2_000;
/** The subject must look placed in this many of the last frames before the countdown starts. */
const ARM_WINDOW = 6;
const ARM_NEEDED = 5;
const DISARM_NEEDED = 3;
/** Below this many usable frames the window has not seen enough of one face to speak for it. */
const MIN_ACCEPTED_FRAMES = 8;

export type FacePhase = 'idle' | 'loading' | 'aiming' | 'countdown' | 'capturing' | 'done';
export type FaceFault = 'CAMERA_DENIED' | 'CAMERA_UNAVAILABLE' | 'INSECURE' | 'ENGINE_FAILED';

/**
 * What the window actually did, as the state machine knows it.
 *
 * The screen says the same thing for the last two — "we couldn't determine a clear face shape" — and
 * for a subject that is the right amount to say. It is not enough for a validation study, which has
 * to tell a classifier being cautious from a capture that never got far enough to ask it, so the
 * distinction is recorded here rather than left to whoever was watching.
 *
 *   CLASSIFIED           the gates passed on enough frames and the classifier named a shape
 *   CLASSIFIER_UNKNOWN   the gates passed on enough frames and the classifier declined
 *   ACQUISITION_FAILURE  the classifier was never reached; `reason` says what stopped it
 */
export type CaptureOutcome = 'CLASSIFIED' | 'CLASSIFIER_UNKNOWN' | 'ACQUISITION_FAILURE';

/**
 * Why a window never reached a classification.
 *
 * `GateFailure` verbatim from the frozen `@optiflow/face-vision` when frames were refused, and the
 * window's own floor when some passed but too few. Nothing is invented and nothing is free text.
 */
export type AcquisitionFailureReason = GateFailure | 'INSUFFICIENT_ACCEPTED_FRAMES';

/** One plain instruction per refusal. The screen never shows a reason code, a degree or a score. */
export type FaceGuidance =
  | 'CENTRE_FACE'
  | 'MOVE_BACK'
  | 'MOVE_CLOSER'
  | 'WHOLE_FACE_VISIBLE'
  | 'LOOK_STRAIGHT'
  | 'HEAD_LEVEL'
  | 'ONE_FACE'
  | 'HOLD_STILL';

const GUIDANCE: Record<GateFailure, FaceGuidance> = {
  NO_FACE: 'CENTRE_FACE',
  MULTIPLE_FACES: 'ONE_FACE',
  INCOMPLETE_OUTLINE: 'CENTRE_FACE',
  NOT_MEASURABLE: 'LOOK_STRAIGHT',
  EXCESSIVE_YAW: 'LOOK_STRAIGHT',
  EXCESSIVE_PITCH: 'LOOK_STRAIGHT',
  EXCESSIVE_ROLL: 'HEAD_LEVEL',
  FACE_TOO_SMALL: 'MOVE_CLOSER',
  ASYMMETRIC_OUTLINE: 'LOOK_STRAIGHT',
  FACE_CROPPED: 'WHOLE_FACE_VISIBLE',
};

export interface FaceAttempt {
  /**
   * The session this capture belongs to, and the attempt within it.
   *
   * A result is only ever offered for the session it was captured under. The desk passes the session
   * id; the phone passes the connection its one-time code redeemed, which names exactly one session.
   * `attemptId` changes on every retake, so a superseded result cannot be sent either.
   */
  sessionKey: string;
  attemptId: string;
  /** The classification the window settled on, or a refusal. */
  analysis: FaceAnalysis;
  /** What the window did, told apart properly rather than by how the screen looked. */
  outcome: CaptureOutcome;
  /** Present exactly when the outcome is ACQUISITION_FAILURE. */
  failureReason: AcquisitionFailureReason | null;
  /** How the capture went. Not a quality score, and not evidence about the shape. */
  acceptedFrameCount: number;
  captureDurationMs: number;
  /** True when the window produced a suggestion the desk can be shown: exactly `outcome === 'CLASSIFIED'`. */
  usable: boolean;
}

export interface FaceCapture {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  phase: FacePhase;
  guidance: FaceGuidance | null;
  placed: boolean;
  countdown: number | null;
  progress: number;
  attempt: FaceAttempt | null;
  fault: FaceFault | null;
  attemptCount: number;
  start: () => void;
  retry: () => void;
  stop: () => void;
}

const faultOf = (error: unknown): FaceFault => {
  if (error instanceof CameraError) {
    if (error.kind === 'DENIED') return 'CAMERA_DENIED';
    if (error.kind === 'INSECURE') return 'INSECURE';
    return 'CAMERA_UNAVAILABLE';
  }
  return 'ENGINE_FAILED';
};

/**
 * What the window did, from what it counted rather than from what it showed.
 *
 * Three questions in order, and the order is the point. Did any frame survive the gates — if none
 * did, the reason is the gate that refused the last one, which is the same closed vocabulary the
 * guidance above is built from. Did enough survive — if some did but fewer than the floor, that is
 * the window's own refusal and it has its own name. Only then does what the classifier said matter,
 * and UNKNOWN there is the classifier answering, not the capture failing.
 */
function outcomeOf(
  acceptedCount: number,
  chosen: FaceAnalysis | null,
): { outcome: CaptureOutcome; failureReason: AcquisitionFailureReason | null } {
  if (acceptedCount === 0) {
    const refused = chosen?.geometry.failures[0] ?? GateFailure.NOT_MEASURABLE;
    return { outcome: 'ACQUISITION_FAILURE', failureReason: refused };
  }
  if (acceptedCount < MIN_ACCEPTED_FRAMES) {
    return { outcome: 'ACQUISITION_FAILURE', failureReason: 'INSUFFICIENT_ACCEPTED_FRAMES' };
  }
  if (chosen === null) {
    return { outcome: 'ACQUISITION_FAILURE', failureReason: GateFailure.NOT_MEASURABLE };
  }
  return chosen.suggested === FaceShape.UNKNOWN
    ? { outcome: 'CLASSIFIER_UNKNOWN', failureReason: null }
    : { outcome: 'CLASSIFIED', failureReason: null };
}

/** The instruction for one refused frame: the first failure the gates reported, in the order they check. */
function guidanceFor(analysis: FaceAnalysis | null): FaceGuidance {
  const first = analysis?.geometry.failures[0];
  return first ? (GUIDANCE[first] ?? 'CENTRE_FACE') : 'HOLD_STILL';
}

export function useFaceCapture(sessionKey: string | null): FaceCapture {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [phase, setPhase] = useState<FacePhase>('idle');
  const [guidance, setGuidance] = useState<FaceGuidance | null>(null);
  const [placed, setPlaced] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [attempt, setAttempt] = useState<FaceAttempt | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [fault, setFault] = useState<FaceFault | null>(null);

  // The per-frame loop touches refs only: a state update per camera frame would re-render constantly.
  const phaseRef = useRef<FacePhase>('idle');
  const streamRef = useRef<MediaStream | null>(null);
  const engineRef = useRef<Landmarker | null>(null);
  const wakeRef = useRef<WakeLockSentinel | null>(null);
  const recentRef = useRef<boolean[]>([]);
  const frameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // The session a running capture is for, read inside the loop where `sessionKey` would be a stale closure.
  const sessionRef = useRef<string | null>(sessionKey);
  const attemptIdRef = useRef<string | null>(null);

  const toPhase = useCallback((next: FacePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const release = useCallback(() => {
    stopCamera(streamRef.current);
    streamRef.current = null;
    void wakeRef.current?.release().catch(() => undefined);
    wakeRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const nextVideoFrame = useCallback(
    () =>
      new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (!done) {
            done = true;
            resolve();
          }
        };
        const v = videoRef.current as
          (HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }) | null;
        if (v?.requestVideoFrameCallback) v.requestVideoFrameCallback(finish);
        else requestAnimationFrame(finish);
        setTimeout(finish, 120);
      }),
    [],
  );

  /** One frame: draw, locate, judge. The pixels and the landmarks go out of scope here. */
  const analyseFrame = useCallback((): FaceAnalysis | null => {
    const video = videoRef.current;
    const engine = engineRef.current;
    if (!video || !engine || !video.videoWidth) return null;

    const w = video.videoWidth;
    const h = video.videoHeight;
    const canvas = (frameCanvasRef.current ??= document.createElement('canvas'));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.drawImage(video, 0, 0, w, h);

    // The same reading a photo gets, so the two inputs cannot drift apart.
    return analyseDetection(
      toFaces(engine.landmarker.detectForVideo(canvas, performance.now())),
      w,
      h,
    );
  }, []);

  const remember = useCallback((ok: boolean) => {
    const recent = recentRef.current;
    recent.push(ok);
    if (recent.length > ARM_WINDOW) recent.shift();
  }, []);

  const armed = () => recentRef.current.filter(Boolean).length >= ARM_NEEDED;
  const disarmed = () => recentRef.current.filter((ok) => !ok).length >= DISARM_NEEDED;

  /** The window: collect what the gates accept, then let the classifier speak for the outline they agree on. */
  const runWindow = useCallback(async () => {
    toPhase('capturing');
    setCountdown(null);
    setGuidance('HOLD_STILL');
    setProgress(0);

    const startedAt = performance.now();
    const accepted: FaceAnalysis[] = [];
    // Whose capture this is. Both are compared again at the end, so a session that changes hands
    // mid-window throws the window away rather than publishing a result under the new session's name.
    const forSession = sessionRef.current;
    const forAttempt = attemptIdRef.current;

    for (;;) {
      await nextVideoFrame();
      if (phaseRef.current !== 'capturing') return;
      const analysis = analyseFrame();
      const now = performance.now();
      if (analysis && analysis.geometry.failures.length === 0 && analysis.features) {
        accepted.push(analysis);
      }
      const elapsed = now - startedAt;
      setProgress(Math.min(1, elapsed / FACE_WINDOW_MS));
      if (elapsed >= FACE_WINDOW_MS) {
        const durationMs = Math.round(elapsed);
        // The middle frame of what was accepted: one real analysis rather than an average of several,
        // so the reason codes and the scores belong to a single outline and still describe each other.
        const chosen =
          accepted.length > 0 ? (accepted[Math.floor(accepted.length / 2)] ?? null) : analysis;
        const { outcome, failureReason } = outcomeOf(accepted.length, chosen);
        // Unchanged in value and in meaning: the desk is shown a suggestion for exactly the captures
        // it was shown one for before. Only the reason behind a refusal is now written down.
        const usable = outcome === 'CLASSIFIED';

        release();
        if (
          forSession === null ||
          sessionRef.current !== forSession ||
          attemptIdRef.current !== forAttempt
        ) {
          // The session or the attempt moved on while this window ran. Nothing here belongs to what is on screen now.
          toPhase('idle');
          setProgress(0);
          setGuidance(null);
          return;
        }

        toPhase('done');
        setProgress(1);
        setGuidance(null);
        setAttempt(
          chosen
            ? {
                sessionKey: forSession,
                attemptId: forAttempt as string,
                analysis: chosen,
                outcome,
                failureReason,
                acceptedFrameCount: accepted.length,
                captureDurationMs: durationMs,
                usable,
              }
            : null,
        );
        setAttemptCount((n) => n + 1);
        return;
      }
    }
  }, [analyseFrame, nextVideoFrame, release, toPhase]);

  const countdownThenCapture = useCallback(async () => {
    toPhase('countdown');
    for (let n = Math.round(COUNTDOWN_MS / 1000); n >= 1; n -= 1) {
      setCountdown(n);
      const until = performance.now() + 1000;
      while (performance.now() < until) {
        await nextVideoFrame();
        if (phaseRef.current !== 'countdown') return;
        const analysis = analyseFrame();
        if (!analysis) continue;
        remember(analysis.geometry.failures.length === 0);
        if (disarmed()) {
          setCountdown(null);
          setGuidance(guidanceFor(analysis));
          void aim();
          return;
        }
      }
    }
    setCountdown(null);
    void runWindow();
    // `aim` and this call each other; the ref-held phase is what ends either loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyseFrame, nextVideoFrame, remember, runWindow, toPhase]);

  /** Preview: judge placement only. Nothing is classified and nothing is shown but an instruction. */
  const aim = useCallback(async () => {
    toPhase('aiming');
    recentRef.current = [];
    setCountdown(null);
    setProgress(0);
    while (phaseRef.current === 'aiming') {
      await nextVideoFrame();
      if (phaseRef.current !== 'aiming') return;
      const analysis = analyseFrame();
      if (!analysis) continue;
      const ok = analysis.geometry.failures.length === 0;
      remember(ok);
      setGuidance(ok ? 'HOLD_STILL' : guidanceFor(analysis));
      const ready = armed();
      setPlaced(ready);
      if (ready) {
        void countdownThenCapture();
        return;
      }
    }
  }, [analyseFrame, countdownThenCapture, nextVideoFrame, remember, toPhase]);

  const begin = useCallback(async () => {
    if (phaseRef.current !== 'idle' && phaseRef.current !== 'done') return;
    if (sessionRef.current === null) return;
    setFault(null);
    // A retake supersedes the result before the camera even opens, so the previous suggestion cannot
    // be sent while the next attempt is running. There is one live result at a time, or none.
    setAttempt(null);
    attemptIdRef.current = crypto.randomUUID();
    toPhase('loading');

    try {
      const stream = await openCamera(FRONT_CAMERA);
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new CameraError('UNAVAILABLE');
      video.srcObject = stream;
      await video.play();
      if ('wakeLock' in navigator && document.visibilityState === 'visible') {
        wakeRef.current = await navigator.wakeLock.request('screen').catch(() => null);
      }
      engineRef.current ??= await loadFaceLandmarker('GPU');
    } catch (error) {
      release();
      setFault(faultOf(error));
      toPhase('idle');
      return;
    }

    void aim();
  }, [aim, release, toPhase]);

  const stop = useCallback(() => {
    toPhase('idle');
    release();
    setGuidance(null);
    setCountdown(null);
    setPlaced(false);
    setProgress(0);
  }, [release, toPhase]);

  /**
   * A new session starts from nothing.
   *
   * The dialog that hosts this hook is a native `<dialog>`, whose children stay mounted when it
   * closes, so without this the previous session's suggestion would still be on screen — and
   * sendable — the next time it opens. The binding is what makes that impossible; clearing the
   * screen is only what the binding looks like.
   */
  useEffect(() => {
    sessionRef.current = sessionKey;
    attemptIdRef.current = null;
    phaseRef.current = 'idle';
    release();
    recentRef.current = [];
    setPhase('idle');
    setAttempt(null);
    setAttemptCount(0);
    setFault(null);
    setGuidance(null);
    setCountdown(null);
    setPlaced(false);
    setProgress(0);
  }, [sessionKey, release]);

  useEffect(
    () => () => {
      phaseRef.current = 'idle';
      release();
      engineRef.current?.landmarker.close();
      engineRef.current = null;
    },
    [release],
  );

  return {
    videoRef,
    phase,
    guidance,
    placed,
    countdown,
    progress,
    attempt,
    fault,
    attemptCount,
    start: () => void begin(),
    retry: () => void begin(),
    stop,
  };
}

/** The versions every suggestion travels with, so a stored answer can always be explained. */
export const FACE_VERSIONS = {
  classifierVersion: CLASSIFIER_VERSION,
  geometryVersion: GEOMETRY_VERSION,
  gateConfigVersion: GATE_CONFIG_VERSION,
} as const;
